import { NgClass, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCardActions } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatToolbar } from '@angular/material/toolbar';

import * as pdfjsLib from 'pdfjs-dist';

import {
  FormioRendererComponent,
  FormioRendererData,
} from '../../../../components/data-interaction/formio-renderer/formio-renderer.component';
import { DicomViewerComponent } from '../../../../components/dicom-viewer/dicom-viewer.component';
import { LabResultViewerComponent } from '../../../../components/patient-info/lab-result-viewer/lab-result-viewer.component';
import { LabTableViewerComponent } from '../../../../components/patient-info/lab-table-viewer/lab-table-viewer.component';
import { LabResult } from '../../../../models/view-content.models/view-content-clinic-domain.model';
import { ExtendedFinding, ExtendedRegion, FindingItem } from '../../med-view.component';
import { CanvasRendererService } from '../../services/canvas-renderer.service';
import { MediaToolbarComponent } from '../media-toolbar/media-toolbar.component';

@Component({
  selector: 'app-finding-viewer',
  templateUrl: './finding-viewer.component.html',
  styleUrls: ['./finding-viewer.component.scss'],
  standalone: true,
  providers: [CanvasRendererService],
  imports: [
    NgSwitch,
    NgSwitchCase,
    FormioRendererComponent,
    MediaToolbarComponent,
    NgIf,
    NgClass,
    MediaToolbarComponent,
    LabResultViewerComponent,
    MatIconButton,
    LabTableViewerComponent,
    MatIcon,
    MatToolbar,
    DicomViewerComponent,
    MatProgressSpinner,
    MatCardActions,
    MatButton,
  ],
})
export class FindingViewerComponent implements OnChanges {
  @Input({ required: true }) selectedFinding!: FindingItem<any> | undefined;
  @Input() allLabResults!:
    | FindingItem<{ allLabResults: LabResult[]; documentType: string }>
    | undefined;

  @Output() clickOnOpenPaintingTool: EventEmitter<void> = new EventEmitter<void>();
  @Output() clickOnOpenFullscreen: EventEmitter<void> = new EventEmitter<void>();
  @Output() clickOnOpenLabTableFullscreen: EventEmitter<void> = new EventEmitter<void>();
  @Output() makeScreenshot: EventEmitter<ExtendedFinding> = new EventEmitter<ExtendedFinding>();
  @Output() saveForm: EventEmitter<FindingItem<ExtendedFinding>> = new EventEmitter<
    FindingItem<ExtendedFinding>
  >();

  @Input() selectedRegion?: ExtendedRegion;
  @Input() isLaborRegionSelected: boolean = false;
  @Output() openLaborViewer: EventEmitter<void> = new EventEmitter<void>();

  public isLabTableShowLimits = true;
  protected readonly Object = Object;

  constructor(public canvasRendererService: CanvasRendererService) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdf.worker.mjs';
  }

  public async ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedFinding']?.currentValue) {
      switch (this.selectedFinding?.data.documentType) {
        case 'pdf':
          await this.canvasRendererService.initCanvas(
            'previewCanvasPDF',
            'previewWrapperPDF',
            this.selectedFinding,
            this.selectedFinding.data.annotations,
            false,
          );
          break;
        case 'image':
          await this.canvasRendererService.initCanvas(
            'previewCanvasIMG',
            'previewWrapperIMG',
            this.selectedFinding,
            this.selectedFinding.data.annotations,
            false,
          );
          break;
      }
    }

    this.allLabResults = changes['allLabResults']?.currentValue;
  }

  public onClickOnOpenPaintingTool() {
    this.clickOnOpenPaintingTool.emit();
  }

  public onClickOnOpenFullscreen() {
    this.clickOnOpenFullscreen.emit();
  }

  public onClickOnOpenLabTableFullscreen() {
    this.clickOnOpenLabTableFullscreen.emit();
  }

  public clickMakeScreenshot($event: ExtendedFinding) {
    this.makeScreenshot.emit($event);
  }

  public clickSaveForm($event: FindingItem<ExtendedFinding> | undefined) {
    this.saveForm.emit($event);
  }

  public onChangeFormData($event: FormioRendererData[]) {
    if (this.selectedFinding) this.selectedFinding.data.formioRendererData = $event;
  }
  public onClickOpenLaborViewer() {
    this.openLaborViewer.emit();
  }
}
